import React, { useMemo, useCallback } from 'react';
// import { ImageBackground, ActivityIndicator } from 'react-native';
import { YStack, Button, XStack, useTheme, styled } from 'tamagui';
import CheckIcon from '@mui/icons-material/Check';

import { CobuText, CobuLoader } from '@cobu/ui';
import { Group } from '@cobu/api-client';
import { useSelector } from 'react-redux';
import { Strings, URLS } from '../../constants';
import { RootState } from '../../store';

const ActionButton = styled(Button, {
  variants: {
    buttonType: {
      community: {
        backgroundColor: '$cobuTopaz',
        opacity: 1,
      },
      joined: {
        backgroundColor: '$cobuTopaz',
      },
      join: {
        backgroundColor: '$cobuTopaz',
      },
      loading: {
        backgroundColor: '$cobuTopaz',
        opacity: 0.7,
      },
    },
  } as const,

  // Base styles
  m: '$4',
  py: '$2',
  px: '$4',
  br: '$4',
  fd: 'row',
  ai: 'center',
  jc: 'center',
  shac: '$blackTransparent50',
  shof: { width: 0, height: 1 },
  shop: 0.2,
  shar: 2,
  elevation: 2,
});

interface GroupDetailHeaderProps {
  group: Group;
  userIsGroupMember: boolean;
  isJoining: boolean;
  isLeaving: boolean;
  onJoinPress: () => void;
  onLeavePress: () => void;
  canJoin?: boolean;
}

export const GroupDetailHeader: React.FC<GroupDetailHeaderProps> = ({
  group,
  userIsGroupMember,
  isJoining,
  isLeaving,
  onJoinPress,
  onLeavePress,
  canJoin = true,
}) => {
  // Early return if group is not loaded
  if (!group) {
    return null;
  }

  const theme = useTheme();
  const { user } = useSelector((state: any) => state.auth);
  const buildingName = user?.building?.name;

  // Memoize building-wide group check for performance
  const isBuildingWideGroup = useMemo(() => {
    return group.isDefault ||
      group.isMarketplace ||
      group.name === buildingName;
  }, [group?.isDefault, group?.isMarketplace, group?.name, buildingName]);

  // Memoize button configuration to avoid recalculation on every render
  const buttonConfig = useMemo(() => {
    const strings = Strings.get();

    if (isBuildingWideGroup) {
      return {
        text: strings.groupDetail.labels.communityWideGroup,
        buttonType: 'community' as const,
        disabled: true,
        showIcon: false,
      };
    }

    if (userIsGroupMember) {
      return {
        text: isLeaving ? (strings.groupDetail.actions.leaving || '').toUpperCase() : strings.groupDetail.actions.joined,
        buttonType: isLeaving ? 'loading' as const : 'joined' as const,
        disabled: isLeaving,
        showIcon: !isLeaving,
        onPress: onLeavePress,
      };
    }

    return {
      text: isJoining ? (strings.groupDetail.actions.joining || '').toUpperCase() : strings.groupDetail.actions.joinGroup,
      buttonType: isJoining ? 'loading' as const : 'join' as const,
      disabled: isJoining,
      hidden: !canJoin,   // hide the join CTA rather than show it disabled
      showIcon: false,
      onPress: onJoinPress,
    };
  }, [isBuildingWideGroup, userIsGroupMember, isJoining, isLeaving, onJoinPress, onLeavePress, canJoin]);

  // Memoize button press handler to prevent unnecessary re-renders
  const handleButtonPress = useCallback(() => {
    if (buttonConfig.onPress && !buttonConfig.disabled) {
      buttonConfig.onPress();
    }
  }, [buttonConfig.onPress, buttonConfig.disabled]);

  // Fall back to the building image, then a placeholder, matching GroupCard —
  // otherwise a group with no imageUrl renders an empty header band.
  const buildingImage = useSelector((state: RootState) => state.auth.user?.building?.image);
  const headerImageUrl = group?.imageUrl || buildingImage || URLS.GROUP_PLACEHOLDER_IMAGE;

  const bgStyle = useMemo(
    () => ({
      backgroundImage: `url(${headerImageUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    }),
    [headerImageUrl]
  );

  return (
    <YStack h={190} w="100%" jc="flex-end" ai="flex-end">
      <YStack
      w="100%"
      h="100%"
      jc="flex-end"
      ai="flex-end"
      style={bgStyle}
      >
        {!buttonConfig.hidden && (
        <ActionButton
          buttonType={buttonConfig.buttonType}
          onPress={handleButtonPress}
          disabled={buttonConfig.disabled}
          unstyled
        >
          <XStack ai="center" jc="center" gap="$1">
            {/* Loading spinner for join/leave actions */}
            {(isJoining || isLeaving) && (
              <CobuLoader
                size="small"
                color='$color'
              />
            )}

            {/* Check icon for joined state */}
            {buttonConfig.showIcon && userIsGroupMember && (
              <CheckIcon
                width={12}
                height={12}
                style={{ color: theme.white?.val }}
              />
            )}

            <CobuText
              variant="body"
              color="$white"
              ls={0}
            >
              {buttonConfig.text}
            </CobuText>
          </XStack>
        </ActionButton>
        )}
      </YStack>
    </YStack>
  );
}; 